import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload } from "lucide-react";
import { toast } from "sonner";

/** Departamentos (Español / Inglés): nombre, logo, políticas propias y emisor de facturas. */
export const DepartmentsCard = () => {
  const [deps, setDeps] = useState([]);
  const [issuers, setIssuers] = useState([]);
  const [busy, setBusy] = useState(null);
  const inputs = useRef({});

  const load = async () => {
    const [d, i] = await Promise.all([
      api.get("/departments"),
      api.get("/issuers").catch(() => ({ data: [] })),
    ]);
    setDeps(d.data || []);
    setIssuers(i.data || []);
  };
  useEffect(() => { load(); }, []);

  const guardar = async (dep, cambios) => {
    setBusy(dep.id);
    try {
      await api.put(`/departments/${dep.id}`, cambios);
      toast.success("Departamento actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(null);
    }
  };

  const subirLogo = async (dep, file) => {
    if (!file) return;
    setBusy(dep.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/departments/${dep.id}/logo`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el logo");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="p-5 mb-6" data-testid="departments-card">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-brand-blue" /> Departamentos
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Cada departamento tiene su nombre, su logo, sus políticas y su emisor de facturas. El
        departamento se define en cada <strong>programa</strong> y los grupos lo heredan.
      </p>
      <div className="space-y-4">
        {deps.map((d) => (
          <div key={d.id} className="rounded-xl border border-slate-200 p-4"
               data-testid={`department-${d.id}`}>
            <div className="flex items-start gap-4">
              <div className="shrink-0 text-center">
                {d.logo_url ? (
                  <img src={`${API_BASE}${d.logo_url}`} alt={d.name}
                       className="h-14 w-14 object-contain rounded-lg border border-slate-200 bg-white" />
                ) : (
                  <div className="h-14 w-14 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <Building2 className="h-6 w-6" />
                  </div>
                )}
                <input type="file" accept="image/*" className="hidden"
                       ref={(el) => { inputs.current[d.id] = el; }}
                       data-testid={`department-logo-input-${d.id}`}
                       onChange={(e) => subirLogo(d, e.target.files?.[0])} />
                <button type="button" onClick={() => inputs.current[d.id]?.click()}
                        className="mt-1.5 text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`department-logo-btn-${d.id}`}>
                  <Upload className="h-3 w-3" /> Logo
                </button>
              </div>
              <div className="flex-1 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nombre en facturas y panel</Label>
                  <Input defaultValue={d.name} className="mt-1"
                         data-testid={`department-name-${d.id}`}
                         onBlur={(e) => e.target.value !== d.name && guardar(d, { name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Nombre corto</Label>
                  <Input defaultValue={d.short_name} className="mt-1"
                         onBlur={(e) => e.target.value !== d.short_name && guardar(d, { short_name: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Enlace de políticas y condiciones</Label>
                  <Input defaultValue={d.policies_url || ""} className="mt-1"
                         data-testid={`department-policies-${d.id}`}
                         onBlur={(e) => e.target.value !== (d.policies_url || "") && guardar(d, { policies_url: e.target.value })} />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Cambiá también la versión para que las tengan que aceptar de nuevo.
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Versión de políticas</Label>
                  <Input defaultValue={d.policies_version || ""} className="mt-1"
                         data-testid={`department-policies-version-${d.id}`}
                         onBlur={(e) => e.target.value !== (d.policies_version || "") && guardar(d, { policies_version: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Emisor de las facturas</Label>
                  <select value={d.issuer_id || ""} disabled={busy === d.id}
                          data-testid={`department-issuer-${d.id}`}
                          onChange={(e) => guardar(d, { issuer_id: e.target.value })}
                          className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm">
                    <option value="">(el emisor por defecto)</option>
                    {issuers.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} {i.prefix ? `· ${i.prefix}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
